import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { resolveClinicForUser } from '../lib/clinicResolver';
import Sidebar from './Sidebar';
import LoadingSpinner from './LoadingSpinner';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// SVGs for sections
const Icons = {
  today: (
    <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  ),
  week: (
    <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  later: (
    <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
    </svg>
  ),
  expired: (
    <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  )
};

// Extracted Section component
const Section = ({ title, items, onToggle, onShare, onDelete, type }) => {
  const isExpired = type === 'expired';
  
  const getHeaderStyles = () => {
    if (isExpired) return { bg: 'rgba(239,68,68,0.1)', text: '#EF4444', border: '#EF4444' };
    if (type === 'today') return { bg: 'rgba(59,130,246,0.1)', text: '#60A5FA', border: '#374151' };
    if (type === 'week') return { bg: 'rgba(16,185,129,0.1)', text: '#34D399', border: '#374151' };
    return { bg: 'rgba(156,163,175,0.1)', text: '#9CA3AF', border: '#374151' };
  };
  
  const styles = getHeaderStyles();

  return (
    <div className="rounded-xl border p-5 flex flex-col h-full" style={{ backgroundColor: '#111827', borderColor: styles.border }}>
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ backgroundColor: styles.bg }}>
            {Icons[type]}
          </div>
          <h3 className="text-lg font-semibold text-white">{title}</h3>
        </div>
        <span
          className="px-2.5 py-0.5 text-xs font-bold rounded-full"
          style={{ backgroundColor: styles.bg, color: styles.text }}
        >
          {items.length}
        </span>
      </div>
      
      {items.length === 0 ? (
        <div className="flex flex-col items-center justify-center flex-1 py-8 text-gray-500 opacity-60">
          <svg className="w-10 h-10 mb-3 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-sm font-medium">Κανένα εκκρεμές</span>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(item => (
            <div
              key={item.id}
              className={`group flex items-center justify-between p-3.5 rounded-xl border transition-all duration-200 ${
                item.is_done ? 'opacity-50 bg-[#111827] border-[#1F2937]' : 'bg-[#1F2937] border-gray-700 hover:border-gray-500 shadow-sm'
              }`}
              style={{
                borderLeftColor: isExpired && !item.is_done ? '#EF4444' : '',
                borderLeftWidth: isExpired && !item.is_done ? '3px' : '1px'
              }}
            >
              <label className="flex items-center gap-3 text-white cursor-pointer flex-1 min-w-0">
                <div className="relative flex items-center justify-center">
                  <input
                    type="checkbox"
                    checked={!!item.is_done}
                    onChange={() => onToggle(item)}
                    className="peer appearance-none w-5 h-5 border border-gray-500 rounded bg-[#111827] checked:bg-emerald-500 checked:border-emerald-500 cursor-pointer transition-all"
                  />
                  <svg className="absolute w-3.5 h-3.5 text-white opacity-0 peer-checked:opacity-100 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className={`truncate font-medium transition-all ${item.is_done ? 'line-through text-gray-500' : 'text-gray-200'}`}>
                  {item.title}
                </span>
              </label>
              <div className="flex items-center gap-1.5 ml-4">
                {item.due_date && (
                  <span className={`text-xs font-medium px-2 py-1 rounded bg-[#111827] border ${isExpired && !item.is_done ? 'text-red-400 border-red-900/50' : 'text-gray-400 border-gray-700'} whitespace-nowrap mr-2`}>
                    {new Date(item.due_date).toLocaleDateString('el-GR', { day: 'numeric', month: 'short' })}
                  </span>
                )}
                
                <button
                  onClick={() => onShare(item)}
                  title="Κοινοποίηση σε συναδέλφους"
                  className="p-1.5 rounded-lg text-gray-500 hover:text-blue-400 hover:bg-blue-500/10 lg:opacity-0 lg:group-hover:opacity-100 transition-all focus:opacity-100"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                </button>
                <button
                  onClick={() => onDelete(item)}
                  title="Διαγραφή"
                  className="p-1.5 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-500/10 lg:opacity-0 lg:group-hover:opacity-100 transition-all focus:opacity-100"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TodoPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [clinic, setClinic] = useState(null);
  const [todos, setTodos] = useState([]);
  const [newTitle, setNewTitle] = useState('');
  const [newDueDate, setNewDueDate] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const toastTimeoutRef = useRef(null);
  const [coworkers, setCoworkers] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [todoToShare, setTodoToShare] = useState(null);
  const [selectedCoworkers, setSelectedCoworkers] = useState([]);
  const [shareFromForm, setShareFromForm] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [todoToDelete, setTodoToDelete] = useState(null);

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  const showToast = (message, type = 'success') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ show: true, message, type });
    toastTimeoutRef.current = setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 4000);
  };

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (clinic && user) {
      fetchTodos();
      fetchCoworkers();
    }
  }, [clinic, user]);

  const init = async () => {
    try {
      const { data: { user: u } } = await supabase.auth.getUser();
      if (!u) { navigate('/login'); return; }
      setUser(u);
      const { clinic: clinicData } = await resolveClinicForUser(supabase, u.id);
      setClinic(clinicData || null);
    } catch {
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const fetchTodos = async () => {
    try {
      const { data, error } = await supabase
        .from('todos')
        .select('*')
        .eq('user_id', user.id)
        .order('is_done', { ascending: true })
        .order('due_date', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTodos(data || []);
    } catch (err) {
      console.error('Error fetching todos:', err);
      showToast('Σφάλμα φόρτωσης εργασιών', 'error');
    }
  };

  const fetchCoworkers = async () => {
    try {
      const { data: vets, error: vetsError } = await supabase
        .from('vets')
        .select('id, user_id, first_name, last_name, clinic_id, deleted')
        .eq('deleted', false)
        .eq('clinic_id', clinic.id)
        .not('user_id', 'is', null)
        .neq('user_id', user.id);

      if (vetsError) {
        console.error('Error fetching vets:', vetsError);
        setCoworkers([]);
        return;
      }

      setCoworkers(vets || []);
    } catch (err) {
      console.error('Error fetching coworkers:', err);
      setCoworkers([]);
    }
  };

  const addTodo = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) { showToast('Συμπληρώστε τίτλο', 'error'); return; }
    try {
      const { data, error } = await supabase
        .from('todos')
        .insert({
          clinic_id: clinic?.id || null,
          user_id: user.id,
          title: newTitle.trim(),
          due_date: newDueDate || null,
          is_done: false,
        })
        .select()
        .single();
      if (error) throw error;

      if (shareFromForm) {
        setTodoToShare(data);
        setShowShareModal(true);
      }

      setNewTitle('');
      setNewDueDate('');
      setShareFromForm(false);
      await fetchTodos();
      showToast('Προστέθηκε εργασία', 'success');
    } catch (err) {
      console.error('Add todo failed:', err);
      showToast('Σφάλμα προσθήκης', 'error');
    }
  };

  const toggleTodo = async (todo) => {
    setTodos(prev => prev.map(t =>
      t.id === todo.id ? { ...t, is_done: !t.is_done } : t
    ));

    try {
      const { error } = await supabase
        .from('todos')
        .update({ is_done: !todo.is_done })
        .eq('id', todo.id)
        .eq('user_id', user.id);
      if (error) throw error;
    } catch (err) {
      setTodos(prev => prev.map(t =>
        t.id === todo.id ? { ...t, is_done: todo.is_done } : t
      ));
      console.error('Toggle todo failed:', err);
      showToast('Σφάλμα ενημέρωσης', 'error');
    }
  };

  const openDeleteModal = (todo) => {
    setTodoToDelete(todo);
    setShowConfirmModal(true);
  };

  const closeDeleteModal = () => {
    setShowConfirmModal(false);
    setTodoToDelete(null);
  };

  const confirmDelete = async () => {
    if (!todoToDelete) return;

    try {
      const { error } = await supabase
        .from('todos')
        .delete()
        .eq('id', todoToDelete.id)
        .eq('user_id', user.id);
      if (error) throw error;
      await fetchTodos();
      showToast('Διαγράφηκε', 'success');
    } catch (err) {
      console.error('Delete todo failed:', err);
      showToast('Σφάλμα διαγραφής', 'error');
    } finally {
      closeDeleteModal();
    }
  };

  const openShareModal = (todo) => {
    setTodoToShare(todo);
    setSelectedCoworkers([]);
    setShowShareModal(true);
  };

  const closeShareModal = () => {
    setShowShareModal(false);
    setTodoToShare(null);
    setSelectedCoworkers([]);
  };

  const toggleCoworkerSelection = (coworkerId) => {
    setSelectedCoworkers(prev =>
      prev.includes(coworkerId)
        ? prev.filter(id => id !== coworkerId)
        : [...prev, coworkerId]
    );
  };

  const shareTodo = async () => {
    if (selectedCoworkers.length === 0) {
      showToast('Επιλέξτε τουλάχιστον έναν συνάδελφο', 'error');
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('share-todo', {
        body: {
          title: todoToShare.title,
          due_date: todoToShare.due_date,
          coworker_ids: selectedCoworkers,
          clinic_id: clinic?.id,
        }
      });

      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error || 'Unknown error');

      const count = data.shared_count;
      showToast(`Κοινοποιήθηκε σε ${count} συνάδελφο${count > 1 ? 'υς' : ''}`, 'success');
      closeShareModal();
    } catch (err) {
      console.error('Share todo failed:', err);
      showToast('Σφάλμα κοινοποίησης', 'error');
    }
  };

  const groupTodos = () => {
    const today = new Date();
    today.setHours(0,0,0,0);
    const endOfWeek = new Date(today);
    const day = today.getDay();
    const daysUntilEnd = (7 - (day === 0 ? 7 : day));
    endOfWeek.setDate(today.getDate() + daysUntilEnd);

    const buckets = { today: [], week: [], later: [], expired: [] };
    for (const t of todos) {
      if (!t.due_date) { buckets.later.push(t); continue; }
      const due = new Date(t.due_date);
      due.setHours(0,0,0,0);
      if (due < today && !t.is_done) buckets.expired.push(t);
      else if (due.getTime() === today.getTime()) buckets.today.push(t);
      else if (due > today && due <= endOfWeek) buckets.week.push(t);
      else buckets.later.push(t);
    }
    return buckets;
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const { today, week, later, expired } = groupTodos();

  return (
    <div className="min-h-screen text-white flex flex-col lg:flex-row" style={{ backgroundColor: '#1A202C', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, background: 'radial-gradient(800px 800px at 10% -10%, rgba(16,185,129,0.12), transparent), radial-gradient(800px 800px at 110% 10%, rgba(59,130,246,0.08), transparent)' }} className="pointer-events-none" />

      <Sidebar isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />

      {/* Main Content */}
      <div className="flex-1 p-4 lg:p-8">
        {/* Toast */}
        {toast.show && (
          <div className="fixed top-4 right-4 left-4 sm:left-auto z-50">
            <div
              className="flex items-center px-5 py-4 rounded-xl border max-w-sm ml-auto transition-all duration-300"
              style={{
                background: 'linear-gradient(135deg, rgba(17,24,39,0.95) 0%, rgba(31,41,55,0.95) 100%)',
                backdropFilter: 'blur(10px)',
                borderColor: toast.type === 'success' ? '#10B981' : toast.type === 'error' ? '#EF4444' : '#3B82F6',
                boxShadow: `0 10px 25px rgba(0,0,0,0.3), 0 0 20px ${
                  toast.type === 'success' ? 'rgba(16,185,129,0.2)' :
                  toast.type === 'error' ? 'rgba(239,68,68,0.2)' : 'rgba(59,130,246,0.2)'
                }`
              }}
            >
              {toast.type === 'success' && (
                <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3" style={{ background: 'rgba(16,185,129,0.2)' }}>
                  <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
              {toast.type === 'error' && (
                <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3" style={{ background: 'rgba(239,68,68,0.2)' }}>
                  <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              )}
              <span className="text-white font-medium flex-1">{toast.message}</span>
              <button
                onClick={() => setToast({ show: false, message: '', type: 'success' })}
                className="ml-3 text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-6 lg:mb-8">
          <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2">Εργασίες</h1>
          <p className="text-gray-400 text-base lg:text-lg">Οργάνωση εργασιών</p>
        </div>

        {/* Quick Add Todo (Inline Form) */}
        <div className="rounded-xl border p-4 mb-6 shadow-lg" style={{ backgroundColor: '#111827', borderColor: '#374151' }}>
          <form onSubmit={addTodo} className="flex flex-col lg:flex-row items-center gap-4">
            
            {/* Title Input */}
            <div className="flex-1 w-full relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <input 
                type="text" 
                value={newTitle} 
                onChange={(e) => setNewTitle(e.target.value)} 
                maxLength={100} 
                className="w-full pl-10 pr-4 py-3 rounded-lg border text-white font-medium placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition-all" 
                style={{ backgroundColor: '#1F2937', borderColor: '#374151' }} 
                placeholder="Τι πρέπει να γίνει; π.χ. Επικοινωνία με πελάτη" 
              />
            </div>

            {/* Right Side Controls */}
            <div className="flex flex-col sm:flex-row items-center gap-4 w-full lg:w-auto">
              
              {/* Date Picker */}
              <div className="w-full sm:w-48">
                <DatePicker
                  selected={newDueDate ? new Date(newDueDate + 'T00:00:00') : null}
                  onChange={(date) => {
                    if (date) {
                      const yyyy = date.getFullYear();
                      const mm = String(date.getMonth() + 1).padStart(2, '0');
                      const dd = String(date.getDate()).padStart(2, '0');
                      setNewDueDate(`${yyyy}-${mm}-${dd}`);
                    } else {
                      setNewDueDate('');
                    }
                  }}
                  dateFormat="dd/MM/yyyy"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  isClearable
                  placeholderText="Προσθήκη Ημ/νίας"
                  className="w-full px-4 py-3 rounded-lg border text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition-all"
                  calendarClassName="dark-datepicker"
                  wrapperClassName="w-full"
                  style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}
                />
              </div>

              {/* Share Checkbox */}
              <label className="flex items-center gap-2 text-gray-400 cursor-pointer hover:text-white transition-colors w-full sm:w-auto bg-[#1F2937] border border-gray-700 px-4 py-3 rounded-lg">
                <input
                  type="checkbox"
                  checked={shareFromForm}
                  onChange={(e) => setShareFromForm(e.target.checked)}
                  className="peer appearance-none w-4 h-4 border border-gray-500 rounded bg-[#111827] checked:bg-blue-500 checked:border-blue-500 transition-all flex-shrink-0"
                />
                <span className="text-sm font-medium whitespace-nowrap">Κοινοποίηση</span>
              </label>

              {/* Submit Button */}
              <button 
                type="submit" 
                disabled={!newTitle.trim()}
                className="w-full sm:w-auto px-6 py-3 rounded-lg font-semibold text-white transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 shadow-md" 
                style={{ background: 'linear-gradient(90deg, #10B981, #059669)' }}
              >
                Προσθήκη
              </button>

            </div>
          </form>
        </div>

        {/* Progress Stats */}
        {todos.length > 0 && (
          <div className="rounded-xl border p-5 mb-6 flex flex-col sm:flex-row items-center justify-between gap-6 shadow-sm" style={{ backgroundColor: '#111827', borderColor: '#374151' }}>
            <div className="flex items-center gap-5">
              <div className="text-center">
                <div className="text-2xl font-bold text-white">{todos.filter(t => t.is_done).length}</div>
                <div className="text-xs text-gray-400 uppercase tracking-wide mt-1">Ολοκληρωμενες</div>
              </div>
              <div className="text-gray-600 text-3xl font-light">/</div>
              <div className="text-center">
                <div className="text-2xl font-bold text-white">{todos.length}</div>
                <div className="text-xs text-gray-400 uppercase tracking-wide mt-1">Συνολο</div>
              </div>
            </div>
            <div className="flex-1 max-w-2xl w-full">
              <div className="flex items-center gap-4">
                <div className="flex-1 h-3 rounded-full overflow-hidden bg-[#1F2937] border border-gray-700">
                  <div
                    className="h-full rounded-full transition-all duration-1000 ease-out relative overflow-hidden"
                    style={{
                      width: `${todos.length > 0 ? (todos.filter(t => t.is_done).length / todos.length) * 100 : 0}%`,
                      background: 'linear-gradient(90deg, #10B981, #3B82F6)'
                    }}
                  >
                    <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                  </div>
                </div>
                <span className="text-lg font-bold text-emerald-400 w-12 text-right">
                  {todos.length > 0 ? Math.round((todos.filter(t => t.is_done).length / todos.length) * 100) : 0}%
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Expired Section - Top Priority */}
        {expired.length > 0 && (
          <div className="mb-6">
            <Section title="Ληγμένες" items={expired} onToggle={toggleTodo} onShare={openShareModal} onDelete={openDeleteModal} type="expired" />
          </div>
        )}

        {/* Sections Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Section title="Σήμερα" items={today} onToggle={toggleTodo} onShare={openShareModal} onDelete={openDeleteModal} type="today" />
          <Section title="Αυτή την εβδομάδα" items={week} onToggle={toggleTodo} onShare={openShareModal} onDelete={openDeleteModal} type="week" />
          <Section title="Αργότερα" items={later} onToggle={toggleTodo} onShare={openShareModal} onDelete={openDeleteModal} type="later" />
          {expired.length === 0 && (
            <Section title="Ληγμένες" items={expired} onToggle={toggleTodo} onShare={openShareModal} onDelete={openDeleteModal} type="expired" />
          )}
        </div>
      </div>

      {/* Mobile overlay */}
      {isSidebarOpen && (
        <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setIsSidebarOpen(false)} />
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={closeShareModal}>
          <div className="rounded-2xl border max-w-md w-full overflow-hidden shadow-2xl" style={{ backgroundColor: '#111827', borderColor: '#374151' }} onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-800">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(59,130,246,0.15)' }}>
                  <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-white">Κοινοποίηση</h2>
              </div>
              <button onClick={closeShareModal} className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-500 hover:text-white hover:bg-gray-800 transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Task preview */}
            {todoToShare && (
              <div className="px-6 py-5">
                <div className="px-4 py-3 rounded-lg border border-gray-700 bg-[#1F2937] mb-5">
                  <p className="text-white text-sm font-medium">{todoToShare.title}</p>
                  {todoToShare.due_date && (
                    <p className="text-xs text-gray-400 mt-1 flex items-center gap-1.5">
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                      {new Date(todoToShare.due_date).toLocaleDateString('el-GR')}
                    </p>
                  )}
                </div>

                {/* Coworkers list */}
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Επιλογή Συναδέλφων</p>
                  {coworkers.length === 0 ? (
                    <div className="py-6 text-center border border-dashed border-gray-700 rounded-lg bg-[#111827]">
                      <svg className="w-8 h-8 text-gray-600 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                      </svg>
                      <p className="text-gray-400 text-sm font-medium">Κανένας διαθέσιμος</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-56 overflow-y-auto pr-1 custom-scrollbar">
                      {coworkers.map(coworker => {
                        const isSelected = selectedCoworkers.includes(coworker.user_id);
                        const initials = `${(coworker.first_name || '')[0] || ''}${(coworker.last_name || '')[0] || ''}`.toUpperCase();
                        return (
                          <div
                            key={coworker.user_id}
                            onClick={() => toggleCoworkerSelection(coworker.user_id)}
                            className={`flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer transition-all border ${
                              isSelected 
                                ? 'bg-blue-500/10 border-blue-500/30' 
                                : 'bg-[#1F2937] border-gray-700 hover:border-gray-500'
                            }`}
                          >
                            <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ background: isSelected ? '#3B82F6' : '#374151', color: '#fff' }}>
                              {initials}
                            </div>
                            <span className="text-sm font-medium text-white flex-1">{coworker.first_name} {coworker.last_name}</span>
                            <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 border transition-colors ${isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-500 bg-[#111827]'}`}>
                              {isSelected && (
                                <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="flex gap-3 px-6 py-4 border-t border-gray-800 bg-[#1F2937]/50">
              <button
                onClick={closeShareModal}
                className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-300 border border-gray-600 bg-[#111827] hover:bg-gray-800 transition-colors"
              >
                Ακύρωση
              </button>
              <button
                onClick={shareTodo}
                disabled={selectedCoworkers.length === 0}
                className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium text-white transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 shadow-md"
                style={{ background: 'linear-gradient(135deg, #3B82F6, #2563EB)' }}
              >
                Κοινοποίηση
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={closeDeleteModal}>
          <div className="rounded-2xl border max-w-sm w-full overflow-hidden shadow-2xl" style={{ backgroundColor: '#111827', borderColor: '#374151' }} onClick={(e) => e.stopPropagation()}>
            
            <div className="px-6 py-6 text-center">
              <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-4 border border-red-500/20" style={{ background: 'rgba(239,68,68,0.1)' }}>
                <svg className="w-7 h-7 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Διαγραφή Εργασίας;</h2>
              <p className="text-gray-400 text-sm">
                Είστε σίγουροι ότι θέλετε να διαγράψετε οριστικά την παρακάτω εργασία;
              </p>
            </div>

            {/* Task preview */}
            {todoToDelete && (
              <div className="mx-6 mb-6 px-4 py-3 rounded-lg border border-gray-700 bg-[#1F2937]">
                <p className="text-white text-sm font-medium text-center">{todoToDelete.title}</p>
              </div>
            )}

            {/* Footer */}
            <div className="flex gap-3 px-6 py-4 border-t border-gray-800 bg-[#1F2937]/50">
              <button
                onClick={closeDeleteModal}
                className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-300 border border-gray-600 bg-[#111827] hover:bg-gray-800 transition-colors"
              >
                Ακύρωση
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium text-white transition-all hover:scale-[1.02] shadow-md"
                style={{ background: 'linear-gradient(135deg, #EF4444, #DC2626)' }}
              >
                Ναι, Διαγραφή
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TodoPage;